import { uniq } from 'lodash';
import { useContext, useEffect, useState } from 'react';
import { DetailContext, UserContext, abortableFetch } from '@eas/common-web';
import {
  CourtFile,
  DisciplinaryProceedingsFile,
  EsFile,
  InfFile,
  Me,
  PlenaryOpinionFile,
  SprFile,
  UserRole,
} from '@models';
import { Agenda, EsFileState, EvidenceApiUrl, Role } from '@enums';
import { courtFileAccessMap } from '../es-file-court/court-file-access-map';
import { infFileAccessMap } from '../es-file-inf/inf-file-access-map';
import { plenaryOpinionAccessMap } from '../es-file-plenary-opinion/plenary-opinion-access-map';
import { sprFileAccessMap } from '../es-file-spr/spr-file-access-map';
import { disciplinaryProceedingsFileAccessMap } from './../es-file-disciplinary-proceedings/disciplinary-proceedings-access-map';

type PermissionEsFile = EsFile &
  Pick<CourtFile, 'judge' | 'analystUserRole'> &
  Pick<SprFile | InfFile, 'solverUserRoles'>;

/**
 * Volitelný override pro konkrétní UI element.
 *
 * Používá se jen tam, kde je potřeba nad standardní access mapou doplnit
 * speciální pravidlo. `undefined` znamená: pokračuj běžným vyhodnocením mapy.
 */
type PermissionOverlayResolver = (element: string) => boolean | undefined;

/**
 * Standardní FE access mapa spisu: stav spisu -> role -> povolené UIElementy.
 */
type EsFileAccessMap = Map<EsFileState, Map<Role, string[]>>;

type HasEsFilePermissionParams = {
  accessMap?: EsFileAccessMap;
  esFile?: PermissionEsFile | null;
  user?: Me | null;
  isExisting?: boolean;
  element: string;
  skipAssignmentCheck?: boolean;
  skipAssignmentRoles?: Role[];
  overlayResolver?: PermissionOverlayResolver;
};

export const INF_FILES_ACCESS = [
  Role.MAILROOM, // Podatelna/výpravna
  Role.LUSTRUM, // Lustrum
  Role.HEAD_OSV, // Vedoucí OSV
  Role.INDEX, // Rejstřík
  Role.LAWYER, // Právník
  Role.SECRETARY, // Tajemnice
  Role.JUDGE, // Soudce
  Role.ASSISTANT, // Asistent
  Role.ANALYST, // Analytik
  Role.FILE_ROOM, // Spisovna
  Role.SOLVER, // Řešitel
  Role.READER, // Čtenář
];

export const SPR_FILES_ACCESS = INF_FILES_ACCESS;

export const COURT_FILES_ACCESS = [
  Role.LUSTRUM, // Lustrum
  Role.HEAD_OSV, // Vedoucí OSV
  Role.INDEX, // Rejstřík
  Role.LAWYER, // Právník
  Role.SECRETARY, // Tajemnice
  Role.JUDGE, // Soudce
  Role.ASSISTANT, // Asistent
  Role.ANALYST, // Analytik
  Role.FILE_ROOM, // Spisovna
  Role.READER, // Čtenář
  Role.ADVISER, // Poradce - sbírky
  Role.PUBLICATION, // Zveřejnění
  Role.ANONYMIZATION, // Anonymizace
  Role.VIEWING_REQUEST_MANAGER, // Správce žádostí o nahlížení
];

export const DISCIPLINARY_PROCEEDINGS_FILE_ACCESS = [
  Role.INDEX, // Rejstřík,
  Role.JUDGE, // Soudce
  Role.READER, // Čtenář
];

export const PLENARY_OPINION_FILES_ACCESS = [
  Role.LUSTRUM, // Lustrum
  Role.HEAD_OSV, // Vedoucí OSV
  Role.INDEX, // Rejstřík
  Role.LAWYER, // Právník
  Role.SECRETARY, // Tajemnice
  Role.JUDGE, // Soudce
  Role.ASSISTANT, // Asistent
  Role.ANALYST, // Analytik
  Role.FILE_ROOM, // Spisovna
  Role.READER, // Čtenář
  Role.ADVISER, // Poradce - sbírky
];

export const ALL_FILES_ACCESS = uniq([
  ...INF_FILES_ACCESS,
  ...COURT_FILES_ACCESS,
  ...DISCIPLINARY_PROCEEDINGS_FILE_ACCESS,
  ...PLENARY_OPINION_FILES_ACCESS,
]);

export function getRolesForAgenda(agenda: Agenda): Role[] {
  switch (agenda) {
    case Agenda.COURT:
      return COURT_FILES_ACCESS;
    case Agenda.SPR:
      return SPR_FILES_ACCESS;
    case Agenda.INF:
      return INF_FILES_ACCESS;
    case Agenda.DISCIPLINARY_PROCEEDINGS:
      return DISCIPLINARY_PROCEEDINGS_FILE_ACCESS;
    case Agenda.PLENARY_OPINION:
      return PLENARY_OPINION_FILES_ACCESS;
    default:
      return [];
  }
}

/**
 * Vrátí access mapu pro typ spisu podle jeho agendy.
 *
 * Používá se hlavně mimo detail samotného spisu (např. v detailu rozhodnutí),
 * kde máme k dispozici jen `esFile` a potřebujeme použít stejný permission
 * model jako evidence daného typu spisu.
 *
 * Neznámá nebo nepodporovaná agenda vrací `undefined` a volající má skončit
 * bezpečně bez oprávnění.
 */
export function getEsFileAccessMapForAgenda(agenda?: Agenda) {
  switch (agenda) {
    case Agenda.COURT:
      return courtFileAccessMap;
    case Agenda.PLENARY_OPINION:
      return plenaryOpinionAccessMap;
    default:
      return undefined;
  }
}

/**
 * Čisté vyhodnocení oprávnění na jeden UI element spisu.
 *
 * Jde o původní logiku z `usePermission`, jen bez React contextů. Díky tomu ji
 * lze sdílet mezi detailem spisu a jinými moduly, které editují stejnou entitu
 * `esFile` (např. detail rozhodnutí).
 *
 * Pravidla:
 * - SUPERADMIN má vždy přístup.
 * - Bez stavu spisu se oprávnění nepovolí.
 * - Pokud overlay vrátí boolean, má přednost před access mapou.
 * - Jinak se oprávnění skládá z access mapy podle stavu spisu a rolí uživatele.
 * - Assignment kontroly pro INDEX/SECRETARY/ASSISTANT/JUDGE/SOLVER zůstávají stejné
 *   jako v evidenci spisů.
 */
export function hasEsFilePermission({
  accessMap,
  esFile,
  user,
  isExisting = false,
  element,
  skipAssignmentCheck,
  skipAssignmentRoles,
  overlayResolver,
}: HasEsFilePermissionParams): boolean {
  const isSuperAdmin = (user?.userRoles ?? []).some(
    (r) => r.role === Role.SUPERADMIN
  );

  if (isSuperAdmin) {
    return true;
  }

  if (!esFile?.state) {
    return false;
  }

  const overlayPermission = overlayResolver?.(element);

  if (overlayPermission !== undefined) {
    return overlayPermission;
  }

  const isAdmin = (user?.userRoles ?? []).some((r) => r.role === Role.ADMIN);
  const shouldSkipAssignmentCheck = skipAssignmentCheck ?? isAdmin;

  const permittedElements = new Set<string>();

  const userIdPresent = (
    esFileUserRoles: UserRole[],
    currentUserId?: string
  ): boolean =>
    !!currentUserId &&
    esFileUserRoles.some((role) => role?.user?.id === currentUserId);

  user?.userRoles.forEach(({ role }) => {
    const permittedForRole = accessMap?.get(esFile.state!)?.get(role!);
    const shouldSkipAssignmentForRole =
      shouldSkipAssignmentCheck || skipAssignmentRoles?.includes(role!);

    // Skip assignment checks for roles explicitly exempted for this element.
    if (!shouldSkipAssignmentForRole) {
      if (
        role === Role.INDEX &&
        isExisting &&
        esFile?.indexUserRole?.user?.id !== user.id &&
        esFile?.indexUserRole?.user?.id !==
          user.additionalDetails?.activeSubstitution?.substitutedUser?.id
      ) {
        return;
      }

      if (
        role === Role.SECRETARY &&
        isExisting &&
        esFile?.secretaryUserRole?.user?.id !== user.id &&
        esFile?.secretaryUserRole?.user?.id !==
          user.additionalDetails?.activeSubstitution?.substitutedUser?.id
      ) {
        return;
      }

      if (
        role === Role.ASSISTANT &&
        isExisting &&
        !userIdPresent(esFile?.assistantUserRoles ?? [], user.id) &&
        !userIdPresent(
          esFile?.assistantUserRoles ?? [],
          user.additionalDetails?.activeSubstitution?.substitutedUser?.id
        )
      ) {
        return;
      }

      if (
        role === Role.SOLVER &&
        isExisting &&
        !userIdPresent(esFile?.solverUserRoles ?? [], user.id) &&
        !userIdPresent(
          esFile?.solverUserRoles ?? [],
          user.additionalDetails?.activeSubstitution?.substitutedUser?.id
        )
      ) {
        return;
      }

      if (
        role === Role.JUDGE &&
        isExisting &&
        esFile?.judge?.judgeUserRole?.user?.id !== user.id &&
        esFile?.judge?.judgeUserRole?.user?.id !==
          user.additionalDetails?.activeSubstitution?.substitutedUser?.id
      ) {
        return;
      }

      if (
        role === Role.ANALYST &&
        esFile?.agenda === Agenda.COURT &&
        isExisting &&
        esFile?.analystUserRole?.user?.id !== user.id &&
        esFile?.analystUserRole?.user?.id !==
          user.additionalDetails?.activeSubstitution?.substitutedUser?.id
      ) {
        return;
      }
    }

    permittedForRole?.forEach((element) => permittedElements.add(element));
  });

  return permittedElements.has(element);
}

/**
 * Sdílené vyhodnocení spisového oprávnění podle `esFile.agenda`.
 *
 * Toto je hlavní vstup pro moduly mimo evidenci spisů. Například rozhodnutí
 * nejdřív namapuje své pole na odpovídající spisový `UIElement` a následně zde
 * ověří, jestli je tento element editovatelný podle stejné access mapy, jakou
 * používá detail spisu.
 *
 * Pokud agenda nemá podporovanou access mapu nebo chybí stav spisu, funkce
 * vrací `false` (fail-closed).
 */
export function hasSharedEsFilePermission({
  esFile,
  user,
  isExisting,
  element,
  skipAssignmentCheck,
  skipAssignmentRoles,
}: Omit<HasEsFilePermissionParams, 'accessMap' | 'overlayResolver'>): boolean {
  const accessMap = getEsFileAccessMapForAgenda(esFile?.agenda);

  if (!esFile?.state || !accessMap) {
    return false;
  }

  return hasEsFilePermission({
    accessMap,
    esFile,
    user,
    isExisting,
    element,
    skipAssignmentCheck,
    skipAssignmentRoles,
  });
}

/**
 * React hook pro detail konkrétního typu spisu.
 *
 * Načte aktuálního uživatele a stav detailu z contextů a nad předanou access
 * mapou vrátí `hasPermission(element)`. Veřejné API zůstává stejné pro všechny
 * existující spisové obrazovky.
 */
export function usePermission(
  accessMap?: EsFileAccessMap,
  esFile?: PermissionEsFile | null,
  overlayResolver?: PermissionOverlayResolver
) {
  const { user } = useContext<UserContext<Me>>(UserContext);
  const { isExisting } = useContext(DetailContext);

  const isSuperAdmin = (user?.userRoles ?? []).some(
    (r) => r.role === Role.SUPERADMIN
  );

  if (isSuperAdmin) {
    return {
      hasPermission: () => true,
    };
  }

  const isAdmin = (user?.userRoles ?? []).some((r) => r.role === Role.ADMIN);

  const hasPermission = (
    element: string,
    skipAssignmentCheck = isAdmin,
    skipAssignmentRoles?: Role[]
  ) =>
    hasEsFilePermission({
      accessMap,
      esFile,
      user,
      isExisting,
      element,
      skipAssignmentCheck,
      skipAssignmentRoles,
      overlayResolver: (requestedElement) => {
        const taskElement = requestedElement === 'TASKS';
        if (taskElement && esFile?.state) {
          return hasEsFilePermission({
            accessMap,
            esFile:
              esFile.state === EsFileState.CLOSED
                ? {
                    ...esFile,
                    state:
                      esFile.agenda === Agenda.COURT
                        ? EsFileState.REGISTER
                        : EsFileState.IN_PROGRESS,
                  }
                : esFile,
            user,
            isExisting,
            element:
              esFile.agenda === Agenda.PLENARY_OPINION
                ? 'TASKS'
                : 'TASKS_AND_DEADLINES',
            skipAssignmentCheck,
            skipAssignmentRoles,
          });
        }
        return overlayResolver?.(requestedElement);
      },
    });

  return {
    hasPermission,
  };
}

/**
 * Ověří, zda aktuální uživatel smí vidět koncepty daného spisu.
 *
 * Na rozdíl od běžných field permissions se zde oprávnění dotazuje backendu,
 * protože dostupnost konceptů není řízená jen statickou FE access mapou.
 */
export function useDraftsVisibility(esFile?: Pick<EsFile, 'id'> | null) {
  const { user } = useContext<UserContext<Me>>(UserContext);
  const [draftsAccess, setDraftsAccess] = useState<{
    esFileId?: string;
    allowed: boolean;
  }>({ allowed: false });

  const isSuperAdmin = (user?.userRoles ?? []).some(
    (r) => r.role === Role.SUPERADMIN
  );

  useEffect(() => {
    if (isSuperAdmin) {
      setDraftsAccess({ esFileId: esFile?.id, allowed: true });
      return;
    }

    if (!esFile?.id) {
      setDraftsAccess({ allowed: false });
      return;
    }

    const esFileId = esFile.id;

    setDraftsAccess({ esFileId, allowed: false });

    let canceled = false;

    abortableFetch(`${EvidenceApiUrl.ES_FILES}/${esFileId}/draft/access`)
      .json()
      .then((allowed) => {
        if (!canceled) {
          setDraftsAccess({ esFileId, allowed: Boolean(allowed) });
        }
      })
      .catch(() => {
        if (!canceled) {
          setDraftsAccess({ esFileId, allowed: false });
        }
      });

    return () => {
      canceled = true;
    };
  }, [esFile?.id, isSuperAdmin]);

  const canViewDrafts =
    draftsAccess.esFileId === esFile?.id && draftsAccess.allowed;

  return { canViewDrafts };
}

/** Permission hook pro soudní spis. */
export function useCourtFilePermission(esFile?: CourtFile | null) {
  return usePermission(courtFileAccessMap, esFile);
}

/** Permission hook pro SPR spis. */
export function useSprFilePermission(esFile?: SprFile | null) {
  return usePermission(sprFileAccessMap, esFile);
}

/** Permission hook pro INF spis. */
export function useInfFilePermission(esFile?: InfFile | null) {
  return usePermission(infFileAccessMap, esFile);
}

/** Permission hook pro kárné řízení. */
export function useDisciplinaryProceedingsPermission(
  esFile?: DisciplinaryProceedingsFile | null
) {
  return usePermission(disciplinaryProceedingsFileAccessMap, esFile);
}

/** Permission hook pro stanovisko pléna. */
export function usePlenaryOpinionPermission(
  esFile?: PlenaryOpinionFile | null
) {
  return usePermission(plenaryOpinionAccessMap, esFile);
}
